import { BreakpointsProvider, DEFAULT_BREAKPOINTS } from '@platform-ui/media';
import { Typography } from '@platform-ui/styles';
import React, { Suspense, useEffect, useLayoutEffect, useMemo } from 'react';
import { Poll, TrueTheme } from 'schema/src/types/poll.generated';
import { useMediaQuery } from 'usehooks-ts';
import { ErrorBoundary } from './components/ErrorBoundary';
import { NewLoaderOverlay } from './components/NewLoaderOverlay';
import { TEMPLATE_WEBVIEW } from './constants';
import { CryptoContextProvider } from './contexts/cryptoContext';
import { ErrorTrackingContextProvider } from './contexts/errorTrackingContext';
import { VariableContextProvider } from './contexts/variableContext';
import { injectDebugTools, isDebugModeEnabled } from './debug';
import { DynamicConfig } from './DynamicConfigGuard';
import { FeatureFlagContextProvider } from './featureFlag';
import { LocaleProvider } from './locale';
import { PollEntrypointLoader } from './PollEntrypointLoader';
import { applyTuiOrTdsThemeHtmlAttribute, getPollTemplateForTheme, getPrefersScheme } from './themes/themeUtils';
import { getUrlParam } from './utils/get-url-param';
import { inIframe, isAndroid, isIOS, isPollsSite, isWebview } from './utils/location';
import { notifyParent } from './utils/notifyIframe';

const COLOR_SCHEME_QUERY = '(prefers-color-scheme: dark)';

export const PollAppContexts = ({
  poll,
  theme,
  title,
}: {
  poll: Poll;
  theme: TrueTheme['data'];
  title: string | null;
}) => {
  const { template } = poll;

  const isWebviewTemplate = template === TEMPLATE_WEBVIEW;

  const isPreview = getUrlParam('is_preview') === 'true';

  injectDebugTools();

  const prefersColorScheme = usePrefersColorScheme({ template, theme });

  let machineInspector = false;
  try {
    machineInspector = localStorage.getItem('useMachineInspect') === 'true' && isDebugModeEnabled();
  } catch {
    /* empty */
  }
  // to enable: localStorage.setItem('useMachineInspect', 'true'), localStorage.setItem('DEBUG_MODE_ON', 'true')

  useEffect(() => {
    const handleFocus = () => {
      notifyParent({ type: 'survey_iframe_focused' });
    };

    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const isWebviewPreview = isWebviewTemplate && isPreview;

  const getTypographyPlatform = () => {
    if (isWebviewPreview) return 'android';
    if (inIframe()) return 'desktop';
    if (isPollsSite()) return 'desktop';
    if (isWebview()) {
      if (isIOS()) return 'ios';
      if (isAndroid()) return 'android';
    }

    return 'desktop';
  };

  return (
    <Suspense fallback={<NewLoaderOverlay hideContentId flat isWebview={isWebviewTemplate} />}>
      <FeatureFlagContextProvider flags={{ machineInspector }}>
        <BreakpointsProvider breakpoints={DEFAULT_BREAKPOINTS} defaultBreakpoint="desktopM">
          <Typography platform={getTypographyPlatform()} style={{ height: '100%', overflowY: 'auto' }}>
            <ErrorTrackingContextProvider>
              <LocaleProvider>
                <ErrorBoundary isWebview={isWebviewTemplate}>
                  <VariableContextProvider>
                    <CryptoContextProvider poll={poll}>
                      <DynamicConfig>
                        <PollEntrypointLoader
                          poll={poll}
                          title={title}
                          theme={theme}
                          prefersColorScheme={prefersColorScheme}
                        />
                      </DynamicConfig>
                    </CryptoContextProvider>
                  </VariableContextProvider>
                </ErrorBoundary>
              </LocaleProvider>
            </ErrorTrackingContextProvider>
          </Typography>
        </BreakpointsProvider>
      </FeatureFlagContextProvider>
    </Suspense>
  );
};

const usePrefersColorScheme = ({
  template,
  theme,
}: {
  template: Poll['template'];
  theme: Partial<TrueTheme['data']>;
}) => {
  const isDarkTheme = useMediaQuery(COLOR_SCHEME_QUERY);
  const themeTemplate = getPollTemplateForTheme(template);

  const prefersColorScheme = useMemo(() => {
    const isForceDark = theme[themeTemplate]?.forceColorScheme === 'dark';
    const isForceLight = theme[themeTemplate]?.forceColorScheme === 'light';

    return getPrefersScheme({ isForceDark, isForceLight });
  }, [theme, themeTemplate, isDarkTheme]);

  useLayoutEffect(() => {
    applyTuiOrTdsThemeHtmlAttribute(theme, themeTemplate);
  }, [theme, themeTemplate, prefersColorScheme]);

  return prefersColorScheme;
};
